import { useState, useCallback, useMemo } from "react";
import { FieldProps } from "@firecms/core";
import { TextField, Tooltip, IconButton, Select, SelectItem } from "@firecms/ui";

// Characters excluding ambiguous ones (O/0, I/1, l)
const CHARS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

function generateCode(prefix: string, length: number): string {
    let result = "";
    for (let i = 0; i < length; i++) {
        result += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
    }
    return prefix ? `${prefix}-${result}` : result;
}

function formatNumber(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toString();
}

interface PromoCodeFieldProps {
    prefix?: string;
    suffixLength?: number;
}

export function PromoCodeField({
    value,
    setValue,
    error,
    showError,
    disabled,
    autoFocus,
    property,
    customProps,
}: FieldProps<string, PromoCodeFieldProps>) {
    const cfgPrefix = customProps?.prefix ?? "PROMO";
    const cfgSuffixLength = customProps?.suffixLength ?? 5;

    const [prefix, setPrefix] = useState(cfgPrefix);
    const [suffixLength, setSuffixLength] = useState<string>(String(cfgSuffixLength));
    const [showConfig, setShowConfig] = useState(false);
    const [flash, setFlash] = useState(false);

    const numLength = parseInt(suffixLength);
    const totalCombinations = useMemo(() => Math.pow(CHARS.length, numLength), [numLength]);

    const handleGenerate = useCallback(() => {
        const trimmedPrefix = prefix.toUpperCase().trim();
        const code = generateCode(trimmedPrefix, numLength);
        setValue(code);
        setFlash(true);
        setTimeout(() => setFlash(false), 800);
    }, [prefix, numLength, setValue]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setValue(e.target.value.toUpperCase());
        },
        [setValue],
    );

    const hasError = showError && !!error;
    const description = property.description;

    return (
        <div className="space-y-2">
            {/* Main input with generate button */}
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <TextField
                        value={value ?? ""}
                        onChange={handleChange}
                        label={property.name}
                        error={hasError}
                        disabled={disabled}
                        autoFocus={autoFocus}
                        placeholder="e.g. PROMO-A3KWV"
                        size="medium"
                        className={`w-full transition-all ${flash ? "ring-2 ring-indigo-500/50 rounded" : ""}`}
                        endAdornment={
                            <div className="flex items-center gap-0.5">
                                <Tooltip title="Configure code format" side="top">
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowConfig(!showConfig)}
                                        toggled={showConfig}
                                    >
                                        <span className="text-base">⚙</span>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Generate a random code" side="top">
                                    <IconButton
                                        size="small"
                                        onClick={handleGenerate}
                                        disabled={disabled}
                                    >
                                        <span className="text-base">🎲</span>
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    />
                </div>
            </div>

            {/* Config panel (collapsible) */}
            {showConfig && (
                <div className="flex flex-wrap items-end gap-3 px-3 py-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 text-sm">
                    <div className="min-w-[100px]">
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 font-medium">Prefix</label>
                        <TextField
                            value={prefix}
                            onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                            size="smallest"
                            placeholder="PROMO"
                            className="w-28"
                        />
                    </div>
                    <div className="min-w-[100px]">
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 font-medium">Suffix length</label>
                        <Select
                            value={suffixLength}
                            onValueChange={(v) => setSuffixLength(v)}
                            renderValue={(v) => `${v} chars`}
                            size="small"
                        >
                            <SelectItem value="4">4 characters</SelectItem>
                            <SelectItem value="5">5 characters</SelectItem>
                            <SelectItem value="6">6 characters</SelectItem>
                            <SelectItem value="8">8 characters</SelectItem>
                        </Select>
                    </div>
                    <span className="text-xs text-slate-400 dark:text-slate-500 pb-2">
                        {formatNumber(totalCombinations)} unique combinations
                    </span>
                </div>
            )}

            {/* Description / error */}
            {hasError && <p className="text-xs text-red-500">{error}</p>}
            {description && !hasError && (
                <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
            )}
        </div>
    );
}
